import { parseDate, roundToXDecimals } from '@ais-ui/utils';
import { QueryPointResult } from './queryPointResult';

export interface BaseStationTargetInfo {
  country: string;
  flag: string;
  lastAisReport: Date | null;
  lastAisLatitude: string;
  lastAisLongitude: string;
  lastAisSatLatitude: string;
  lastAisSatLongitude: string;
  lastAisSatReport: Date | null;
  lastOrigin: string;
  latitudeDegrees: string;
  longitudeDegrees: string;
  latitude: number;
  longitude: number;
  mmsi: string;
  name: string;
  targetType: string;
  vdlLoad: string;
}

const desiredTargetNames: string[] = [
  // New names
  'Last Terrestrial Latitude',
  'Last Terrestrial Longitude',
  'Last Satellite Latitude',
  'Last Satellite Longitude',
  'Last Latitude',
  'Last Longitude',
  // Old names (backward compatibility)
  'Last AIS Latitude',
  'Last AIS Longitude',
  'Last AIS-Sat Latitude',
  'Last AIS-Sat Longitude',
  'Latitude',
  'Longitude',
];

const desiredTargetIds: string[] = [
  'country',
  'flag',
  'mmsi',
  'name',
  'vdlload',
  'lastorigin',
  // New IDs
  'datetime_terrestrial_last_report',
  'datetime_satellite_last_report',
  'datetime_last_report',
  'lat_terrestrial_last_report',
  'lon_terrestrial_last_report',
  'lat_satellite_last_report',
  'lon_satellite_last_report',
  'lat_last_report',
  'lon_last_report',
  // Old IDs (backward compatibility)
  'lastaisreport',
  'lastaissatreport',
];

export function extractBasestationTargetInfos(
  result: QueryPointResult,
): BaseStationTargetInfo[] {
  const baseStationTargetInfos: BaseStationTargetInfo[] = [];
  for (const target of result.targetInfo) {
    const rawBasestationTargetInfo: { [key: string]: string } = {};
    for (const target_value of target.values) {
      // Here we map values by their ID
      if (desiredTargetIds.includes(target_value.id)) {
        rawBasestationTargetInfo[target_value.id] = target_value.value;
      }
      // Some values have the same ID so here we map those by the NAME instead
      if (desiredTargetNames.includes(target_value.name)) {
        rawBasestationTargetInfo[target_value.name] = target_value.value;
      }
    }
    const mmsi = target['mmsi'] || rawBasestationTargetInfo['MMSI'];

    const baseStationInfo: BaseStationTargetInfo = {
      country: rawBasestationTargetInfo['country'],
      flag: rawBasestationTargetInfo['flag'],
      lastAisReport: parseDate(
        rawBasestationTargetInfo['datetime_terrestrial_last_report'] ??
          rawBasestationTargetInfo['lastaisreport'],
      ),
      lastAisLatitude:
        rawBasestationTargetInfo['Last Terrestrial Latitude'] ??
        rawBasestationTargetInfo['lat_terrestrial_last_report'] ??
        rawBasestationTargetInfo['Last AIS Latitude'],
      lastAisLongitude:
        rawBasestationTargetInfo['Last Terrestrial Longitude'] ??
        rawBasestationTargetInfo['lon_terrestrial_last_report'] ??
        rawBasestationTargetInfo['Last AIS Longitude'],
      lastAisSatLatitude:
        rawBasestationTargetInfo['Last Satellite Latitude'] ??
        rawBasestationTargetInfo['lat_satellite_last_report'] ??
        rawBasestationTargetInfo['Last AIS-Sat Latitude'],
      lastAisSatLongitude:
        rawBasestationTargetInfo['Last Satellite Longitude'] ??
        rawBasestationTargetInfo['lon_satellite_last_report'] ??
        rawBasestationTargetInfo['Last AIS-Sat Longitude'],
      lastAisSatReport: parseDate(
        rawBasestationTargetInfo['datetime_satellite_last_report'] ??
          rawBasestationTargetInfo['lastaissatreport'],
      ),
      lastOrigin: rawBasestationTargetInfo['lastorigin'],
      latitudeDegrees: rawBasestationTargetInfo['Latitude'],
      longitudeDegrees: rawBasestationTargetInfo['Longitude'],
      latitude: target['lat']
        ? roundToXDecimals(target['lat'], 4)
        : target['lat'],
      longitude: target['lon']
        ? roundToXDecimals(target['lon'], 4)
        : target['lon'],
      mmsi: mmsi,
      name: rawBasestationTargetInfo['name'],
      targetType: target['type'],
      vdlLoad: rawBasestationTargetInfo['vdlload'],
    };
    baseStationTargetInfos.push(baseStationInfo);
  }

  return baseStationTargetInfos;
}
